import {
  Button,
  Dialog,
  Flex,
  SegmentedControl,
  Switch,
  Text,
} from "@radix-ui/themes";

import type {
  AtdNavPosition,
  AtdTheme,
  AtdWidgetGroupLinePosition,
} from "./useAtdPreferences";

/**
 * The selected item sits on an `--accent-9` indicator, and Radix leaves its
 * label at the root's high-contrast gray — unreadable on yellow. `*-contrast`
 * is the step Radix pairs with `*-9` for exactly this.
 */
const SELECTED_ITEM = "data-[state=on]:text-[color:var(--accent-contrast)]";

export interface AtdSettingsPresentationProps {
  open: boolean;
  theme: AtdTheme;
  navPosition: AtdNavPosition;
  widgetGroupLinePosition: AtdWidgetGroupLinePosition;
  onOpenChange: (open: boolean) => void;
  onDarkModeChange: (checked: boolean) => void;
  onNavPositionChange: (position: AtdNavPosition) => void;
  onWidgetGroupLinePositionChange: (
    position: AtdWidgetGroupLinePosition,
  ) => void;
}

export const AtdSettingsPresentation = ({
  open,
  theme,
  navPosition,
  widgetGroupLinePosition,
  onOpenChange,
  onDarkModeChange,
  onNavPositionChange,
  onWidgetGroupLinePositionChange,
}: AtdSettingsPresentationProps) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Content maxWidth="440px">
      <Dialog.Title size="3" weight="bold" mb="1">
        Settings
      </Dialog.Title>
      <Dialog.Description size="2" color="gray" mb="4">
        Preferences for this prototype.
      </Dialog.Description>

      <Text
        as="div"
        size="1"
        color="gray"
        weight="medium"
        mb="2"
        className="uppercase tracking-[0.06em]"
      >
        Appearance
      </Text>

      <Flex align="center" justify="between" gap="4">
        <Flex direction="column" gap="1" className="min-w-0">
          <Text as="label" htmlFor="atd-dark-mode" size="2" weight="medium">
            Dark mode
          </Text>
          <Text size="1" color="gray">
            Switches the whole app between the light and dark palettes.
          </Text>
        </Flex>
        <Switch
          id="atd-dark-mode"
          color="yellow"
          checked={theme === "dark"}
          onCheckedChange={onDarkModeChange}
        />
      </Flex>

      <Text
        as="div"
        size="1"
        color="gray"
        weight="medium"
        mt="5"
        mb="2"
        className="uppercase tracking-[0.06em]"
      >
        Navigation
      </Text>

      <Flex align="center" justify="between" gap="4">
        <Flex direction="column" gap="1" className="min-w-0">
          <Text size="2" weight="medium">
            Position
          </Text>
          <Text size="1" color="gray">
            Show navigation as a left rail or a bar across the top.
          </Text>
        </Flex>
        <SegmentedControl.Root
          size="1"
          className="[--segmented-control-indicator-background-color:var(--accent-9)]"
          value={navPosition}
          aria-label="Navigation position"
          onValueChange={(value) => {
            if (value === "left" || value === "top") {
              onNavPositionChange(value);
            }
          }}
        >
          <SegmentedControl.Item value="left" className={SELECTED_ITEM}>
            Left
          </SegmentedControl.Item>
          <SegmentedControl.Item value="top" className={SELECTED_ITEM}>
            Top
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      <Text
        as="div"
        size="1"
        color="gray"
        weight="medium"
        mt="5"
        mb="2"
        className="uppercase tracking-[0.06em]"
      >
        Widgets
      </Text>

      <Flex align="center" justify="between" gap="4">
        <Flex direction="column" gap="1" className="min-w-0">
          <Text size="2" weight="medium">
            Group color line
          </Text>
          <Text size="1" color="gray">
            Draw the group line along the top or bottom edge of the widget
            handle, or hide it.
          </Text>
        </Flex>
        <SegmentedControl.Root
          size="1"
          className="[--segmented-control-indicator-background-color:var(--accent-9)]"
          value={widgetGroupLinePosition}
          aria-label="Widget group line position"
          onValueChange={(value) => {
            if (value === "top" || value === "bottom" || value === "off") {
              onWidgetGroupLinePositionChange(value);
            }
          }}
        >
          <SegmentedControl.Item value="top" className={SELECTED_ITEM}>
            Top
          </SegmentedControl.Item>
          <SegmentedControl.Item value="bottom" className={SELECTED_ITEM}>
            Bottom
          </SegmentedControl.Item>
          <SegmentedControl.Item value="off" className={SELECTED_ITEM}>
            Off
          </SegmentedControl.Item>
        </SegmentedControl.Root>
      </Flex>

      <Flex justify="end" mt="5">
        <Dialog.Close>
          <Button type="button" variant="soft" color="gray">
            Close
          </Button>
        </Dialog.Close>
      </Flex>
    </Dialog.Content>
  </Dialog.Root>
);
